import { useQuery } from "@tanstack/react-query";

interface GeoLocationResponse {
  ip: string;
  country: string;
}

interface GeoLocationInfo {
  ip: string;
  countryCode: string;
  countryName: string;
}

interface LanguageInfo {
  language: string;
  fullLanguage: string;
}

interface LocationContext {
  ip: string;
  countryCode: string;
  countryName: string;
  language: string;
}

/**
 * Hook pour détecter le pays de l'utilisateur via son IP
 * Utilise l'API https://api.country.is et Intl.DisplayNames
 */
export function useGeoLocationInfo(): { data: GeoLocationInfo | undefined; isLoading: boolean; error: Error | null } {
  const { language } = useLanguageInfo();

  return useQuery({
    queryKey: ["geolocation"],
    queryFn: async (): Promise<GeoLocationInfo> => {
      const response = await fetch("https://api.country.is");
      if (!response.ok) {
        throw new Error("Failed to fetch geolocation");
      }
      const geoData: GeoLocationResponse = await response.json();

      // Utiliser Intl.DisplayNames pour obtenir le nom du pays
      let countryName: string;
      try {
        const regionNames = new Intl.DisplayNames([language], { type: "region" });
        countryName = regionNames.of(geoData.country) || geoData.country;
      } catch {
        // Fallback si la langue n'est pas supportée
        try {
          const regionNames = new Intl.DisplayNames(["fr"], { type: "region" });
          countryName = regionNames.of(geoData.country) || geoData.country;
        } catch {
          // Dernière fallback : utiliser le code pays
          countryName = geoData.country;
        }
      }

      return {
        ip: geoData.ip,
        countryCode: geoData.country,
        countryName,
      };
    },
    staleTime: 1000 * 60 * 60, // 1 heure - la géolocalisation ne change pas souvent
    retry: 1, // Un seul retry en cas d'échec
    refetchOnWindowFocus: false, // Pas besoin de refetch au focus
  });
}

/**
 * Hook pour obtenir les informations de langue du navigateur
 */
export function useLanguageInfo(): LanguageInfo {
  // Récupérer la langue du navigateur
  const browserLanguage = typeof navigator !== "undefined" ? navigator.language : "fr-FR";
  const defaultLanguage = "fr";

  // Extraire la langue principale (ex: "fr-FR" -> "fr")
  const language = browserLanguage.split("-")[0] || defaultLanguage;

  return {
    language,
    fullLanguage: browserLanguage,
  };
}

/**
 * Hook de convenance qui combine géolocalisation + langue
 * Pour les cas où on a besoin de tout en une fois
 */
export function useLocationContext(): LocationContext | undefined {
  const { data: geoInfo } = useGeoLocationInfo();
  const languageInfo = useLanguageInfo();

  if (!geoInfo) return undefined;

  return {
    ip: geoInfo.ip,
    countryCode: geoInfo.countryCode,
    countryName: geoInfo.countryName,
    language: languageInfo.language,
  };
}
